/**
 * Other Fees Table Component
 * Displays other fee collections irrespective of academic year
 * Filters for transactions with isMiscFee = true in feePaidDetails
 */
import React, { Component } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import LoadingComponent from '../../loadingComponent';
import NewFeeReciept from '../../finance/newFeeReciept';

export class OtherFeesTable extends Component {
    constructor(props) {
        super(props);
        this.state = {
            transactions: [],
            flattenedData: [],
            isLoading: false,
            isEnableReceipt: false,
            receiptData: [],
            originalTransactions: [] // Store original API data
        };

        // Create refs for scroll synchronization
        this.dataScrollRef = React.createRef();
        this.grandTotalScrollRef = React.createRef();

        // Bind methods
        this.syncScroll = this.syncScroll.bind(this);
        this.processTransactionsData = this.processTransactionsData.bind(this);
        this.flattenTransactions = this.flattenTransactions.bind(this);
        this.transformApiDataToTableFormat = this.transformApiDataToTableFormat.bind(this);
    }

    componentDidMount() {
        console.log('OtherFeesTable component mounted');
        const { transactionsData, transactionsLoading } = this.props;

        if (transactionsData) {
            this.processTransactionsData(transactionsData);
        } else if (transactionsLoading === false) {
            this.setState({ isLoading: false, transactions: [], flattenedData: [] });
        }
    }

    componentDidUpdate(prevProps) {
        const { transactionsData, transactionsLoading } = this.props;

        if (transactionsData !== prevProps.transactionsData && transactionsData) {
            this.processTransactionsData(transactionsData);
        } else if (transactionsLoading === false && prevProps.transactionsLoading === true && !transactionsData) {
            this.setState({ isLoading: false, transactions: [], flattenedData: [] });
        }
    }

    componentWillUnmount() {
        // Clean up scroll event listener
        const grandTotalWrapper = this.grandTotalScrollRef.current?.querySelector('.p-datatable-wrapper');
        if (grandTotalWrapper) {
            grandTotalWrapper.removeEventListener('scroll', this.syncScroll);
        }
    }

    attachScrollListener = () => {
        const grandTotalWrapper = this.grandTotalScrollRef.current?.querySelector('.p-datatable-wrapper');
        if (grandTotalWrapper) {
            grandTotalWrapper.removeEventListener('scroll', this.syncScroll);
            grandTotalWrapper.addEventListener('scroll', this.syncScroll);
            console.log('Scroll listener attached to grand total');
        }
    }

    syncScroll(e) {
        const scrollLeft = e.target.scrollLeft;
        const dataWrapper = this.dataScrollRef.current?.querySelector('.p-datatable-wrapper');
        if (dataWrapper) {
            dataWrapper.scrollLeft = scrollLeft;
            console.log('Syncing scroll - scrollLeft:', scrollLeft);
        }
    }

    processTransactionsData(data) {
        console.log('OtherFees - Processing transactions data:', data);

        const transactions = data?.collections;
        console.log('OtherFees - Transactions from collections:', transactions);

        if (transactions && Array.isArray(transactions) && transactions.length > 0) {
            // Filter for transactions that have feePaidDetails with isMiscFee = true
            const otherFeesTransactions = [];

            transactions.forEach(transaction => {
                const feePaidDetails = transaction.feePaidDetails;

                if (feePaidDetails && Array.isArray(feePaidDetails)) {
                    // Check if any feePaidDetail has isMiscFee = true
                    const hasMiscFee = feePaidDetails.some(detail => detail.isMiscFee === true);

                    if (hasMiscFee) {
                        otherFeesTransactions.push(transaction);
                    }
                }
            });

            console.log('Filtered other fees (isMiscFee = true):', otherFeesTransactions.length);

            // Transform and flatten the data
            const transformedData = this.transformApiDataToTableFormat(otherFeesTransactions);
            console.log('OtherFeesTable - Transformed Data:', transformedData);

            const flattened = this.flattenTransactions(transformedData);
            console.log('OtherFeesTable - Flattened Data:', flattened);

            this.setState({
                transactions: transformedData,
                flattenedData: flattened,
                originalTransactions: otherFeesTransactions, // Store original data
                isLoading: false
            }, () => {
                setTimeout(() => {
                    this.attachScrollListener();
                }, 100);
            });
        } else {
            console.warn('No valid transactions in collections');
            this.setState({
                transactions: [],
                flattenedData: [],
                originalTransactions: [],
                isLoading: false
            });
        }
    }

    flattenTransactions = (transactionGroups) => {
        const flattened = [];
        let globalSNo = 1;

        transactionGroups.forEach((group, groupIndex) => {
            const feeDetails = group.feeDetails || [];
            const totalRows = feeDetails.length;

            feeDetails.forEach((feeDetail, feeIndex) => {
                flattened.push({
                    sNo: globalSNo,
                    feeDetail: feeDetail,
                    groupIndex: groupIndex,
                    feeIndex: feeIndex,
                    isFirstRow: feeIndex === 0,
                    isLastRow: feeIndex === totalRows - 1,
                    totalRowSpan: totalRows,
                    transactionId: feeDetail.transactionId,
                    receiptNo: feeDetail.receiptNo,
                    uniqueKey: `${globalSNo}-${feeDetail.receiptNo}-${feeIndex}`,
                    originalCollection: group.originalCollection // Pass through original data
                });
            });

            globalSNo++;
        });

        return flattened;
    };

    transformApiDataToTableFormat = (collections) => {
        if (!collections || collections.length === 0) {
            return [];
        }

        const result = [];
        let sNoCounter = 1;

        collections.forEach((collection) => {
            const transactionId = collection.transctionId || '';
            const receiptNo = collection.receiptNo || 'N/A';
            const receiptDate = collection.receiptDate || null;
            const transactionDate = collection.transactionDate || null;
            const paymentMode = collection.paymentMode || 'N/A';
            const remarks = collection.remarks || null;
            const totalAmountPaid = collection.amountPaid || 0;

            const transactionFeeDetails = [];

            // Each feePaidDetails item with isMiscFee = true becomes a row in the table
            if (collection.feePaidDetails && collection.feePaidDetails.length > 0) {
                collection.feePaidDetails.forEach((feeDetail) => {
                    // Only include misc fees
                    if (feeDetail.isMiscFee === true) {
                        // Handle fee type display - don't show "undefined" for payment term
                        let feeTypeDisplay = feeDetail.feeType || 'N/A';
                        if (feeDetail.paymentTerm && feeDetail.paymentTerm !== 'undefined') {
                            feeTypeDisplay = `${feeTypeDisplay} - ${feeDetail.paymentTerm}`;
                        }
                        
                        transactionFeeDetails.push({
                            transactionId: transactionId,
                            remarks: remarks,
                            receiptNo: receiptNo,
                            receiptDate: receiptDate,
                            transactionDate: transactionDate,
                            paymentMode: paymentMode,
                            feeType: feeTypeDisplay,
                            paidAmount: feeDetail.amountPaying || 0,
                            total: totalAmountPaid
                        });
                    }
                });
            }

            // Only add if there are misc fee details
            if (transactionFeeDetails.length > 0) {
                result.push({
                    sNo: sNoCounter++,
                    transactionId: transactionId,
                    receiptNo: receiptNo,
                    feeDetails: transactionFeeDetails,
                    originalCollection: collection // Store reference to original data
                });
            }
        });

        console.log('Other Fees Groups:', result);
        return result;
    };

    formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        const day = String(date.getDate()).padStart(2, '0');
        const month = date.toLocaleString('en-US', { month: 'short' });
        const year = date.getFullYear();
        return `${day} ${month} ${year}`;
    };

    calculateGrandTotal = () => {
        const { flattenedData } = this.state;
        return flattenedData.reduce((sum, row) => sum + (row.feeDetail.paidAmount || 0), 0);
    };

    // Column body templates
    snoBodyTemplate = (rowData) => {
        if (!rowData.isFirstRow) return '';
        return (
            <span style={{ display: 'block', textAlign: 'center' }}>
                {rowData.sNo}
            </span>
        );
    };

    transactionIdBodyTemplate = (rowData) => {
        if (!rowData.isFirstRow) return '';
        const txnId = rowData.feeDetail.transactionId || 'N/A';
        return (
            <div className="transaction-id-info">
                <div className="transaction-id">{txnId}</div>
                {rowData.feeDetail.remarks && (
                    <div className="remarks">Remarks : {rowData.feeDetail.remarks}</div>
                )}
            </div>
        );
    };

    receiptNoBodyTemplate = (rowData) => {
        if (!rowData.isFirstRow) return '';
        return <span>{rowData.feeDetail.receiptNo}</span>;
    };

    receiptDateBodyTemplate = (rowData) => {
        if (!rowData.isFirstRow) return '';
        return <span>{this.formatDate(rowData.feeDetail.receiptDate)}</span>;
    };

    paymentModeBodyTemplate = (rowData) => {
        if (!rowData.isFirstRow) return '';
        return <span>{rowData.feeDetail.paymentMode}</span>;
    };

    feeTypeBodyTemplate = (rowData) => {
        return <span>{rowData.feeDetail.feeType}</span>;
    };

    paidAmountBodyTemplate = (rowData) => {
        return <span>{rowData.feeDetail.paidAmount.toFixed(2)}</span>;
    };

    totalBodyTemplate = (rowData) => {
        if (!rowData.isFirstRow) return '';
        return <span>{rowData.feeDetail.total.toFixed(2)}</span>;
    };

    actionsBodyTemplate = (rowData) => {
        if (!rowData.isFirstRow) return '';
        return (
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center' }}>
                <i
                    className="pi pi-eye"
                    style={{
                        fontSize: '1rem',
                        color: '#076EFF',
                        cursor: 'pointer'
                    }}
                    title="View Receipt"
                    onClick={() => {
                        console.log('View receipt clicked for:', rowData);
                        
                        // Use the original collection data stored in the rowData
                        const originalTransaction = rowData.originalCollection;
                        
                        if (originalTransaction) {
                            console.log('Original transaction found:', originalTransaction);
                            // Pass the original transaction data directly to the receipt
                            this.setState({
                                isEnableReceipt: true,
                                receiptData: originalTransaction
                            });
                        } else {
                            console.error('Original transaction not found in rowData');
                        }
                    }}
                />
            </div>
        );
    };

    rowClassName = (rowData) => {
        const classes = [];

        // Add class for first row in transaction (to show separator from previous transaction)
        if (rowData.isFirstRow) {
            classes.push('transaction-start-row');
        }

        // Add thick border on the last row of each transaction
        if (rowData.isLastRow) {
            classes.push('transaction-last-row');
        }

        return classes.join(' ');
    };

    render() {
        const { flattenedData, isLoading } = this.state;
        const grandTotal = this.calculateGrandTotal();

        return (
            <div className="other-fees-table-container">
                {/* Note Section */}
                <div className="info-note">
                    <i className="pi pi-info-circle"></i>
                    <span>Note : The Collections which are showing irrespective of academic year</span>
                </div>

                {isLoading ? (
                    <div className="loading-container" style={{ textAlign: 'center', padding: '2rem' }}>
                        <i className="pi pi-spin pi-spinner" style={{ fontSize: '2rem' }}></i>
                        <p>Loading transactions...</p>
                    </div>
                ) : (
                    <div className="payment-mode-table-wrapper">
                        <div ref={this.dataScrollRef} className="data-table-scroll-container">
                            <DataTable
                                value={flattenedData}
                                scrollable
                                scrollHeight="flex"
                                className="payment-mode-table"
                                showGridlines
                                rowClassName={this.rowClassName}
                                emptyMessage="No other fees found"
                            >
                                <Column
                                    header="S.No"
                                    body={this.snoBodyTemplate}
                                    headerClassName="sno-header"
                                    bodyClassName="sno-cell"
                                    style={{ width: '80px', minWidth: '80px' }}
                                />
                                <Column
                                    header="TransactionID"
                                    body={this.transactionIdBodyTemplate}
                                    headerClassName="transaction-id-header"
                                    bodyClassName="transaction-id-cell"
                                    style={{ width: '250px', minWidth: '250px' }}
                                />
                                <Column
                                    header="Receipt No"
                                    body={this.receiptNoBodyTemplate}
                                    headerClassName="receipt-no-header"
                                    bodyClassName="receipt-no-cell"
                                    style={{ width: '150px', minWidth: '150px' }}
                                />
                                <Column
                                    header="Receipt Date"
                                    body={this.receiptDateBodyTemplate}
                                    headerClassName="receipt-date-header"
                                    bodyClassName="receipt-date-cell"
                                    style={{ width: '150px', minWidth: '150px' }}
                                />
                                <Column
                                    header="Payment Mode"
                                    body={this.paymentModeBodyTemplate}
                                    headerClassName="payment-mode-header"
                                    bodyClassName="payment-mode-cell"
                                    style={{ width: '150px', minWidth: '150px' }}
                                />
                                <Column
                                    header="Fee Type"
                                    body={this.feeTypeBodyTemplate}
                                    headerClassName="fee-type-header"
                                    bodyClassName="fee-type-cell"
                                    style={{ width: '280px', minWidth: '280px' }}
                                />
                                <Column
                                    header="Paid Amount"
                                    body={this.paidAmountBodyTemplate}
                                    headerClassName="paid-amount-header"
                                    bodyClassName="paid-amount-cell"
                                    style={{ width: '150px', minWidth: '150px' }}
                                />
                                <Column
                                    header="Total"
                                    body={this.totalBodyTemplate}
                                    headerClassName="total-header"
                                    bodyClassName="total-cell"
                                    style={{ width: '150px', minWidth: '150px' }}
                                />
                                <Column
                                    header="Actions"
                                    body={this.actionsBodyTemplate}
                                    headerClassName="actions-header"
                                    bodyClassName="actions-cell"
                                    style={{ width: '100px', minWidth: '100px' }}
                                />
                            </DataTable>
                        </div>

                        {/* Grand Total Row */}
                        <div ref={this.grandTotalScrollRef}>
                            <DataTable
                                value={[{ total: grandTotal }]}
                                className="payment-mode-grand-total-table"
                                scrollable
                                scrollHeight="flex"
                                showGridlines
                            >
                                <Column body={() => ''} style={{ width: '80px', minWidth: '80px' }} />
                                <Column body={() => ''} style={{ width: '250px', minWidth: '250px' }} />
                                <Column body={() => ''} style={{ width: '150px', minWidth: '150px' }} />
                                <Column body={() => ''} style={{ width: '150px', minWidth: '150px' }} />
                                <Column body={() => ''} style={{ width: '150px', minWidth: '150px' }} />
                                <Column body={() => ''} style={{ width: '280px', minWidth: '280px' }} />
                                <Column body={() => <strong style={{ color: '#076EFF' }}>Total</strong>} style={{ width: '150px', minWidth: '150px', textAlign: 'right', paddingRight: '1rem' }} />
                                <Column body={() => <strong style={{ color: '#076EFF' }}>{grandTotal.toFixed(2)}</strong>} style={{ width: '150px', minWidth: '150px', textAlign: 'left' }} />
                                <Column body={() => ''} style={{ width: '100px', minWidth: '100px' }} />
                            </DataTable>
                        </div>
                    </div>
                )}

                {this.state.isEnableReceipt && (
                    <NewFeeReciept
                        receiptData={this.state?.receiptData}
                        hideFeeReciept={() => {
                            this.setState({
                                isEnableReceipt: false,
                                receiptData: []
                            });
                        }}
                    />
                )}
            </div>
        );
    }
}
